'use client';

import {Box, keyframes} from '@mui/material';
import {alpha, styled, type SxProps, type Theme} from '@mui/material/styles';

export interface OrbitMediaProps {
  image: string;
  alt: string;
  sx?: SxProps<Theme>;
}

// Diameter of the whole graphic (outer orbit ring) in px; scales down to fit.
// Sized so the layer ratios below land on their Figma pixel values — at 583 the
// image renders at 446, matching node 1171-31129.
const SIZE = 583;
const ORBIT_PERIOD = '30s';

// Layer diameters as a % of the footprint (the outer tan orbit ring). Both the
// property-page node (1171-31124) and the Grova hero animation share these
// ratios: orbit ring 583 / dashed ring 523 / glow 480 / image 446.
const RING_ORBIT = 100;
const RING_DASHED = (523.19 / 583.23) * 100; // ≈ 89.7
const GLOW = (480.31 / 583.23) * 100; // ≈ 82.4
const IMAGE = (446 / 583.23) * 100; // ≈ 76.5

const orbit = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(-360deg); }
`;

const anchoredCircle = (scale: number) =>
  ({
    position: 'absolute',
    top: '50%',
    right: 0,
    width: `${scale}%`,
    aspectRatio: '1 / 1',
    transform: 'translateY(-50%)',
    borderRadius: '50%',
    boxSizing: 'border-box',
  }) as const;

const Root = styled(Box)({
  position: 'relative',
  width: '100%',
  maxWidth: SIZE,
  aspectRatio: '1 / 1',
  margin: '0 auto',
});

const OrbitRing = styled(Box)(({theme}) => ({
  ...anchoredCircle(RING_ORBIT),
  border: `1px solid ${theme.palette.colors.border.primary.subtle}`,
  pointerEvents: 'none',
}));

const DashedRing = styled(Box)(({theme}) => ({
  ...anchoredCircle(RING_DASHED),
  border: `1px dashed ${theme.palette.colors.border.neutral.inverse}`,
  pointerEvents: 'none',
}));

const Glow = styled(Box)(({theme}) => {
  const beige = theme.palette.colors.surface.primary.default;
  return {
    ...anchoredCircle(GLOW),
    background: `radial-gradient(circle, ${alpha(beige, 0.2)} 0%, ${alpha(beige, 0.08)} 50%, transparent 75%)`,
    filter: 'blur(24px)',
    pointerEvents: 'none',
  };
});

const ImageEl = styled('img')(({theme}) => {
  const black = theme.palette.colors.base.black;
  return {
    ...anchoredCircle(IMAGE),
    objectFit: 'cover',
    display: 'block',
    boxShadow: `0 34px 103px ${alpha(black, 0.4)}`,
    filter: 'brightness(0.9) contrast(1.08) saturate(0.9)',
    backgroundColor: theme.palette.colors.base.black,
  };
});

const ImageOverlay = styled(Box)(({theme}) => {
  const beige = theme.palette.colors.surface.primary.default;
  const black = theme.palette.colors.base.black;
  return {
    ...anchoredCircle(IMAGE),
    background: [
      `radial-gradient(circle at center, transparent 55%, ${alpha(black, 0.55)} 100%)`,
      `linear-gradient(to top, ${alpha(beige, 0.12)} 0%, transparent 40%)`,
    ].join(', '),
    // The stroke lives here, not on <img>: the image carries a black background
    // and a brightness/contrast filter, both of which would repaint the border.
    border: `${theme.borderWidths.thin}px solid ${alpha(beige, 0.3)}`,
    boxShadow: `inset 0 0 51px ${alpha(black, 0.3)}`,
    pointerEvents: 'none',
  };
});

// Frame that matches the dashed (inner) ring exactly — same right-edge anchor,
// so its centre IS the dashed ring's centre (unlike the footprint centre, which
// only the outer orbit ring shares). The spinner fills it and rotates in place,
// so the dot rides the dashed ring rather than the outer one.
const OrbitTrack = styled(Box)({
  ...anchoredCircle(RING_DASHED),
  pointerEvents: 'none',
});

// Fills the track and spins about its own centre, carrying the dot around the
// dashed ring. Pure rotation (no centring translate) so it rests cleanly.
const OrbitSpin = styled(Box)({
  position: 'absolute',
  inset: 0,
  animation: `${orbit} ${ORBIT_PERIOD} linear infinite`,
  '@media (prefers-reduced-motion: reduce)': {animation: 'none'},
});

const Dot = styled(Box)(({theme}) => ({
  position: 'absolute',
  top: 0,
  left: '50%',
  width: 12,
  height: 12,
  transform: 'translate(-50%, -50%)',
  borderRadius: '50%',
  backgroundColor: theme.palette.colors.icon.primary.default,
  boxShadow: `0 0 12px ${theme.palette.colors.icon.primary.default}`,
}));

export function OrbitMedia({image, alt, sx}: OrbitMediaProps) {
  return (
    <Root sx={sx}>
      <OrbitRing />
      <DashedRing />
      <Glow />
      <ImageEl src={image} alt={alt} loading="lazy" decoding="async" draggable={false} />
      <ImageOverlay aria-hidden />
      <OrbitTrack aria-hidden>
        <OrbitSpin>
          <Dot />
        </OrbitSpin>
      </OrbitTrack>
    </Root>
  );
}

OrbitMedia.displayName = 'OrbitMedia';
